import { useNavigate} from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Spinner from "../../Utils/spinner/loading";
import { useUploadVesselGeneralInfo } from "../utils/uploadgeneralvesselinfo";
import useFetchVesselClassifications from "../utils/useFetchVesselClassifications";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { ReactComponent as VatugIcon } from "../../Asset/black.svg";
import { HiX } from 'react-icons/hi';
import { HiArrowLeft } from 'react-icons/hi';
import "../create_vessels/createvessel.css";

// major seaports by country data
const seaportsData = [
  { country: "algeria", ports: ["algiers", "oran", "annaba", "skikda", "béjaïa", "arzew"] },
  { country: "angola", ports: ["luanda", "lobito", "namibe", "cabinda"] },
  { country: "benin", ports: ["cotonou"] },
  { country: "cameroon", ports: ["douala", "kribi"] },
  { country: "cape verde", ports: ["mindelo", "praia"] },
  { country: "côte d'ivoire (ivory coast)", ports: ["abidjan", "san pedro"] },
  { country: "democratic republic of the congo", ports: ["matadi", "boma"] },
  { country: "djibouti", ports: ["port of djibouti", "port of doraleh"] },
  { country: "egypt", ports: ["alexandria", "port said", "damietta", "suez", "port sokhna"] },
  { country: "equatorial guinea", ports: ["bata", "malabo"] },
  { country: "gabon", ports: ["libreville", "port-gentil", "owendo"] },
  { country: "ghana", ports: ["tema", "takoradi"] },
  { country: "guinea", ports: ["conakry"] },
  { country: "kenya", ports: ["mombasa"] },
  { country: "libya", ports: ["tripoli", "benghazi", "al khums"] },
  { country: "madagascar", ports: ["toamasina", "mahajanga", "antsiranana"] },
  { country: "morocco", ports: ["casablanca", "tangier", "agadir", "nador", "safi"] },
  { country: "mozambique", ports: ["maputo", "beira", "nacala"] },
  { country: "namibia", ports: ["walvis bay", "lüderitz"] },
  { country: "nigeria", ports: ["lagos (apapa & tin can island)", "port harcourt", "calabar", "onne"] },
  { country: "senegal", ports: ["dakar"] },
  { country: "south africa", ports: ["durban", "cape town", "port elizabeth", "east london", "richards bay", "ngqura"] },
  { country: "tanzania", ports: ["dar es salaam", "tanga", "mtwara"] },
  { country: "togo", ports: ["lomé"] }
];


// deck space options (sqm/m²) - realistic vessel ranges
const deckSpaceOptions = [
  "50", "75", "100", "125", "150", "175", "200", "250", "300", "350", "400", "450", "500", 
  "600", "700", "800", "900", "1000", "1200", "1400", "1600", "1800", "2000", "2500", "3000", 
  "3500", "4000", "4500", "5000", "6000", "7000", "8000", "9000", "10000", "12000", "15000+"
];

// deck strength options (mt/m²) - realistic vessel specifications
const deckStrengthOptions = [
  "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5", "6", "7", "8", "9", "10", "12", "15", "18", 
  "20", "25", "30", "35", "40", "45", "50", "60", "70", "80", "90", "100", "120", "150+"
];

// speed options (knots) - realistic vessel speeds by type
const speedOptions = [
  "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", 
  "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "32", "34", "36", "38", "40+"
];

const defaultForm = {
  numberOfBerths: "",
  deckSpace: "",
  deckStrength: "",
  classification: "", 
  yearOfBuilt: "",
  speed: "", 
  portOfRegistry: "",
  callSign: "", 
  officialNumber: "",
  imoNumber: "",
  mmsiNumber: "",
  lengthOverall: "",
  beamMoulded: "",
  draft: "",
  grossTonnage: "",
  cruisingSpeed: "",
  range: "",
  fuelCapacity: "",
  propulsion: "",
  generators: "",
  waterCapacity: "",
  builder: "",
  keelLaid: "",
  bollardPull: "",
  bhp: "",
  numberOfPassengers: "",
  trackable: null,
};

const VesselGeneralInformation = () => {
  const [formField, setFormField] = useState(defaultForm);
  const { mutate, isLoading, error: mutationError } = useUploadVesselGeneralInfo();
  const { data: vesselClassificationsData, isLoading: isFetchingVesselClassifications } = useFetchVesselClassifications();
  const vesselType = localStorage.getItem("selectedVesselType");
  const navigate = useNavigate();

  // Custom dropdown states
  const [isClassificationOpen, setIsClassificationOpen] = useState(false);
  const [isTrackableOpen, setIsTrackableOpen] = useState(false);
  const [isPortOfRegistryOpen, setIsPortOfRegistryOpen] = useState(false);
  const [isDeckSpaceOpen, setIsDeckSpaceOpen] = useState(false);
  const [isDeckStrengthOpen, setIsDeckStrengthOpen] = useState(false);
  const [isSpeedOpen, setIsSpeedOpen] = useState(false);
  
  const [classificationSearchTerm, setClassificationSearchTerm] = useState("");
  const [trackableSearchTerm, setTrackableSearchTerm] = useState("");
  const [portOfRegistrySearchTerm, setPortOfRegistrySearchTerm] = useState("");
  const [deckSpaceSearchTerm, setDeckSpaceSearchTerm] = useState("");
  const [deckStrengthSearchTerm, setDeckStrengthSearchTerm] = useState("");
  const [speedSearchTerm, setSpeedSearchTerm] = useState("");
  
  // Port of registry cascading states
  const [selectedCountry, setSelectedCountry] = useState("");
  const [showingCountries, setShowingCountries] = useState(true);
  
  // Refs for click outside handling
  const classificationRef = useRef(null);
  const trackableRef = useRef(null);
  const portOfRegistryRef = useRef(null);
  const deckSpaceRef = useRef(null);
  const deckStrengthRef = useRef(null);
  const speedRef = useRef(null);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (classificationRef.current && !classificationRef.current.contains(event.target)) {
        setIsClassificationOpen(false);
      }
      if (trackableRef.current && !trackableRef.current.contains(event.target)) {
        setIsTrackableOpen(false);
      }
      if (portOfRegistryRef.current && !portOfRegistryRef.current.contains(event.target)) {
        console.log("Click outside port dropdown - resetting state");
        setIsPortOfRegistryOpen(false);
        // Reset to countries view when dropdown closes
        setShowingCountries(true);
        setSelectedCountry("");
        setPortOfRegistrySearchTerm("");
      }
      if (deckSpaceRef.current && !deckSpaceRef.current.contains(event.target)) {
        setIsDeckSpaceOpen(false);
      }
      if (deckStrengthRef.current && !deckStrengthRef.current.contains(event.target)) {
        setIsDeckStrengthOpen(false);
      }
      if (speedRef.current && !speedRef.current.contains(event.target)) {
        setIsSpeedOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle dropdowns
  const toggleClassificationDropdown = () => {
    setIsClassificationOpen(!isClassificationOpen);
    setIsTrackableOpen(false);
    setIsPortOfRegistryOpen(false);
    setIsDeckSpaceOpen(false);
    setIsDeckStrengthOpen(false);
    setIsSpeedOpen(false);
  };

  const toggleTrackableDropdown = () => {
    setIsTrackableOpen(!isTrackableOpen);
    setIsClassificationOpen(false);
    setIsPortOfRegistryOpen(false);
    setIsDeckSpaceOpen(false);
    setIsDeckStrengthOpen(false);
    setIsSpeedOpen(false);
  };

  const togglePortOfRegistryDropdown = () => {
    setIsPortOfRegistryOpen(!isPortOfRegistryOpen);
    setIsClassificationOpen(false);
    setIsTrackableOpen(false);
    setIsDeckSpaceOpen(false);
    setIsDeckStrengthOpen(false);
    setIsSpeedOpen(false);
  };

  const toggleDeckSpaceDropdown = () => {
    setIsDeckSpaceOpen(!isDeckSpaceOpen);
    setIsClassificationOpen(false);
    setIsTrackableOpen(false);
    setIsPortOfRegistryOpen(false);
    setIsDeckStrengthOpen(false);
    setIsSpeedOpen(false);
  };

  const toggleDeckStrengthDropdown = () => {
    setIsDeckStrengthOpen(!isDeckStrengthOpen);
    setIsClassificationOpen(false);
    setIsTrackableOpen(false);
    setIsPortOfRegistryOpen(false);
    setIsDeckSpaceOpen(false);
    setIsSpeedOpen(false);
  };

  const toggleSpeedDropdown = () => {
    setIsSpeedOpen(!isSpeedOpen);
    setIsClassificationOpen(false);
    setIsTrackableOpen(false);
    setIsPortOfRegistryOpen(false);
    setIsDeckSpaceOpen(false);
    setIsDeckStrengthOpen(false);
  };

  // Filter options based on search terms
  const filteredClassifications = vesselClassificationsData?.data?.filter(classification => 
    classification.toLowerCase().includes(classificationSearchTerm.toLowerCase())
  ) || [];

  // Get unique countries from seaports data
  const countries = seaportsData.map(item => item.country);
  const filteredCountries = countries.filter(country => 
    country.toLowerCase().includes(portOfRegistrySearchTerm.toLowerCase())
  );

  // Get ports for selected country
  const selectedCountryData = seaportsData.find(item => item.country === selectedCountry);
  const availablePorts = selectedCountryData ? selectedCountryData.ports : [];
  const filteredPorts = availablePorts.filter(port => 
    port.toLowerCase().includes(portOfRegistrySearchTerm.toLowerCase())
  );

  const filteredDeckSpaceOptions = deckSpaceOptions.filter(option => 
    option.toLowerCase().includes(deckSpaceSearchTerm.toLowerCase())
  );

  const filteredDeckStrengthOptions = deckStrengthOptions.filter(option => 
    option.toLowerCase().includes(deckStrengthSearchTerm.toLowerCase())
  );

  const filteredSpeedOptions = speedOptions.filter(option => 
    option.toLowerCase().includes(speedSearchTerm.toLowerCase())
  );

  // Trackable options
  const trackableOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" }
  ];

  const filteredTrackableOptions = trackableOptions.filter(option => 
    option.label.toLowerCase().includes(trackableSearchTerm.toLowerCase())
  );

  // Handle classification selection
  const handleClassificationSelect = (classification) => {
    setFormField({
      ...formField,
      classification
    });
    setIsClassificationOpen(false);
    setClassificationSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsClassificationOpen(false);
    }, 0);
  };

  // Handle trackable selection
  const handleTrackableSelect = (value) => {
    setFormField({
      ...formField,
      trackable: value
    });
    setIsTrackableOpen(false);
    setTrackableSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsTrackableOpen(false);
    }, 0);
  };

  // Handle country selection
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowingCountries(false); // Switch to showing ports
    setPortOfRegistrySearchTerm(""); // Clear search term
    
    // Force state update to ensure dropdown stays open
    setTimeout(() => {
      setIsPortOfRegistryOpen(true);
    }, 0);
  };

  // Handle port selection
  const handlePortSelect = (port) => {
    setFormField({
      ...formField,
      portOfRegistry: port // Store only the port name
    });
    setIsPortOfRegistryOpen(false);
    setPortOfRegistrySearchTerm("");
    
    // Reset for next time
    setSelectedCountry("");
    setShowingCountries(true);
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsPortOfRegistryOpen(false);
    }, 0);
  };

  // Handle back to countries
  const handleBackToCountries = () => {
    console.log("Back to countries clicked"); // Debug log
    
    // Clear all port-related state and force re-render
    setSelectedCountry("");
    setPortOfRegistrySearchTerm("");
    setShowingCountries(true);
    
    // Ensure dropdown stays open and force state update
    setIsPortOfRegistryOpen(true);
    
    // Force a re-render by updating state again
    setTimeout(() => {
      setShowingCountries(true);
      console.log("State cleared, showing countries:", true);
    }, 10);
  };

  // Handle deck space selection
  const handleDeckSpaceSelect = (value) => {
    setFormField({
      ...formField,
      deckSpace: value
    });
    setIsDeckSpaceOpen(false);
    setDeckSpaceSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsDeckSpaceOpen(false);
    }, 0);
  };

  // Handle deck strength selection
  const handleDeckStrengthSelect = (value) => {
    setFormField({
      ...formField,
      deckStrength: value
    });
    setIsDeckStrengthOpen(false);
    setDeckStrengthSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsDeckStrengthOpen(false);
    }, 0);
  };

  // Handle speed selection
  const handleSpeedSelect = (value) => {
    setFormField({
      ...formField,
      speed: value
    });
    setIsSpeedOpen(false);
    setSpeedSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsSpeedOpen(false);
    }, 0);
  };

  const {
    numberOfBerths,
    deckSpace,
    deckStrength,
    classification,
    yearOfBuilt,
    speed,
    portOfRegistry,
    callSign,
    officialNumber,
    imoNumber,
    mmsiNumber,
    lengthOverall,
    beamMoulded,
    draft,
    grossTonnage,
    cruisingSpeed,
    range,
    fuelCapacity,
    propulsion,
    generators,
    waterCapacity,
    builder,
    keelLaid,
    bollardPull,
    bhp,
    numberOfPassengers,
    trackable,
  } = formField;

  // Initialize from existing portOfRegistry value
  useEffect(() => {
    if (portOfRegistry && !selectedCountry) {
      // Check if it's a country name
      const matchingCountry = seaportsData.find(item => 
        item.country.toLowerCase() === portOfRegistry.toLowerCase()
      );
      if (matchingCountry) {
        setSelectedCountry(portOfRegistry);
        setShowingCountries(false);
      } else {
        // Check if it's a port name
        const matchingPort = seaportsData.find(item => 
          item.ports.some(port => port.toLowerCase() === portOfRegistry.toLowerCase())
        );
        if (matchingPort) {
          setSelectedCountry(matchingPort.country);
          setShowingCountries(false);
        }
      }
    }
  }, [portOfRegistry, selectedCountry]);

  useEffect(() => {
    if (mutationError) {
      // Show exact server error message instead of generic message
      const errorMessage = mutationError.message || mutationError.response?.data?.message || "An error occurred during submission. Please try again.";
      toast.error(errorMessage);
    }
  }, [mutationError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormField({
      ...formField,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Field name mapping for better error messages
    const fieldNameMap = {
      numberOfBerths: "Number of Berths",
      deckSpace: "Deck Space",
      deckStrength: "Deck Strength",
      classification: "Classification",
      yearOfBuilt: "Year of Built",
      speed: "Speed",
      portOfRegistry: "Port of Registry",
      callSign: "Call Sign",
      officialNumber: "Official Number",
      imoNumber: "IMO Number",
      mmsiNumber: "MMSI Number",
      lengthOverall: "Length Overall",
      beamMoulded: "Beam Moulded",
      draft: "Draft",
      grossTonnage: "Gross Tonnage",
      cruisingSpeed: "Cruising Speed",
      range: "Range",
      fuelCapacity: "Fuel Capacity",
      propulsion: "Propulsion",
      generators: "Generators",
      waterCapacity: "Water Capacity",
      builder: "Builder",
      keelLaid: "Keel Laid",
      bollardPull: "Bollard Pull",
      bhp: "BHP",
      numberOfPassengers: "Number of Passengers",
      trackable: "Trackable"
    };

    // Check for missing required fields
    const missingFields = [];
    for (let key in formField) {
      // Skip validation for `bollardPull` if the vessel type does not require it
      if ((vesselType !== "AHTS 001") && key === "bollardPull") {
        continue;
      }
      
      if (formField[key] === "" || formField[key] === null) {
        missingFields.push(fieldNameMap[key] || key);
      }
    }

    // If there are missing fields, show error and scroll to first missing field
    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.join(", ");
      toast.error(`Please fill in the following required fields: ${missingFieldNames}`);
      
      // Scroll to the first missing field
      const firstMissingField = missingFields[0];
      const fieldKey = Object.keys(fieldNameMap).find(key => fieldNameMap[key] === firstMissingField);
      if (fieldKey) {
        const fieldElement = document.querySelector(`[name="${fieldKey}"]`);
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          fieldElement.focus();
        }
      }
      return;
    }

    const numericFields = [
      "numberOfBerths",
      "deckSpace",
      "deckStrength",
      "speed",
      "lengthOverall",
      "beamMoulded",
      "draft",
      "grossTonnage",
      "cruisingSpeed",
      "range",
      "fuelCapacity",
      "propulsion",
      "waterCapacity",
      "keelLaid",
      "bollardPull",
      "bhp",
      "numberOfPassengers",
    ];

    const formToSubmit = { ...formField };

    // Convert numeric fields to numbers
    numericFields.forEach((field) => {
      if (formToSubmit[field] !== "") {
        formToSubmit[field] = Number(formToSubmit[field]);
      }
    });

    // Remove `bollardPull` if vesselType is not "AHTS 001" or "SEV 001"
    if (vesselType !== "AHTS 001") {
      delete formToSubmit.bollardPull;
    }

    mutate(formToSubmit, {
      onError: (err) => {
        console.error("Server error:", err);
        // Show exact server error message
        const errorMessage = err.message || err.response?.data?.message || "An error occurred while submitting the form";
        toast.error(errorMessage);
      },
    });
  };

  // Helper function to get trackable label
  const getTrackableLabel = () => {
    if (trackable === null) return "Select Trackable";
    return trackable ? "Yes" : "No";
  };

  const handleGoBack = () => {
    navigate("/vessel_infomation");
  };

  return ( 
    <div className="create-vessel-container">
      <div className="create-vessel-header-bar">
        <div className="logo-container">
          <VatugIcon />
          <div className="logo-underline"></div>
        </div>
        <div className="title">Vessel Upload</div>
        <button className="close-btn" onClick={() => navigate("/dashboard/myvessels")}> <HiX /> </button>
      </div>

      <div className="create-vessel-card">
        <h2>Vessel General Information</h2>
        <div className="inputs">
          <label>
            Number of Berths
            <input type="text" name="numberOfBerths" value={numberOfBerths} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Deck Space (SQM/M²)
            <div className="custom-select-container" ref={deckSpaceRef}>
              <button 
                type="button"
                className="custom-select-button"
                onClick={toggleDeckSpaceDropdown}
              >
                {deckSpace || "Select Deck Space"}
              </button>
              {isDeckSpaceOpen && (
                <div className="custom-select-dropdown show">
                  <div className="custom-select-search">
                    <input
                      type="text"
                      placeholder="Search deck space..."
                      value={deckSpaceSearchTerm}
                      onChange={(e) => setDeckSpaceSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredDeckSpaceOptions.length > 0 ? (
                    filteredDeckSpaceOptions.map((option, index) => (
                      <div
                        key={index}
                        className={`custom-select-option ${formField.deckSpace === option ? 'selected' : ''}`}
                        onClick={() => handleDeckSpaceSelect(option)}
                      >
                        {option} SQM/M²
                      </div>
                    ))
                  ) : (
                    <div className="custom-select-option">No options found</div>
                  )}
                </div>
              )}
            </div>
          </label>
          <label>
            Deck Strength (MT/M²)
            <div className="custom-select-container" ref={deckStrengthRef}>
              <button 
                type="button"
                className="custom-select-button"
                onClick={toggleDeckStrengthDropdown}
              >
                {deckStrength || "Select Deck Strength"}
              </button>
              {isDeckStrengthOpen && (
                <div className="custom-select-dropdown show">
                  <div className="custom-select-search">
                    <input
                      type="text"
                      placeholder="Search deck strength..."
                      value={deckStrengthSearchTerm}
                      onChange={(e) => setDeckStrengthSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredDeckStrengthOptions.length > 0 ? (
                    filteredDeckStrengthOptions.map((option, index) => (
                      <div
                        key={index}
                        className={`custom-select-option ${formField.deckStrength === option ? 'selected' : ''}`}
                        onClick={() => handleDeckStrengthSelect(option)}
                      >
                        {option} MT/M²
                      </div>
                    ))
                  ) : (
                    <div className="custom-select-option">No options found</div>
                  )}
                </div>
              )}
            </div>
          </label>
          <label>
            Classification
            {isFetchingVesselClassifications ? (
              <Spinner />
            ) : (
              <div className="custom-select-container" ref={classificationRef}>
                <button 
                  type="button"
                  className="custom-select-button"
                  onClick={toggleClassificationDropdown}
                >
                  {classification || "Select Classification"}
                </button>
                {isClassificationOpen && (
                  <div className="custom-select-dropdown show">
                    <div className="custom-select-search">
                      <input
                        type="text"
                        placeholder="Search classification..."
                        value={classificationSearchTerm}
                        onChange={(e) => setClassificationSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {filteredClassifications.length > 0 ? (
                      filteredClassifications.map((classification, index) => (
                        <div
                          key={index}
                          className={`custom-select-option ${formField.classification === classification ? 'selected' : ''}`}
                          onClick={() => handleClassificationSelect(classification)}
                        >
                          {classification}
                        </div>
                      ))
                    ) : (
                      <div className="custom-select-option">No classifications found</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </label>
          <label>
            Year of Built
            <input type="text" name="yearOfBuilt" value={yearOfBuilt} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Speed (Knots)
            <div className="custom-select-container" ref={speedRef}>
              <button 
                type="button"
                className="custom-select-button"
                onClick={toggleSpeedDropdown}
              >
                {speed || "Select Speed"}
              </button>
              {isSpeedOpen && (
                <div className="custom-select-dropdown show">
                  <div className="custom-select-search">
                    <input
                      type="text"
                      placeholder="Search speed..."
                      value={speedSearchTerm}
                      onChange={(e) => setSpeedSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredSpeedOptions.length > 0 ? (
                    filteredSpeedOptions.map((option, index) => (
                      <div
                        key={index}
                        className={`custom-select-option ${formField.speed === option ? 'selected' : ''}`}
                        onClick={() => handleSpeedSelect(option)}
                      >
                        {option} Knots
                      </div>
                    ))
                  ) : (
                    <div className="custom-select-option">No options found</div>
                  )}
                </div>
              )}
            </div>
          </label>
          <label>
            Port of Registry
            <div className="custom-select-container" ref={portOfRegistryRef}>
              <button 
                type="button"
                className="custom-select-button"
                onClick={togglePortOfRegistryDropdown}
              >
                {portOfRegistry || (showingCountries ? "Select Country" : "Select Port")}
              </button>
              {isPortOfRegistryOpen && (
                <div className="custom-select-dropdown show">
                  <div className="custom-select-search">
                    <input
                      type="text"
                      placeholder={showingCountries ? "Search countries..." : "Search ports..."}
                      value={portOfRegistrySearchTerm}
                      onChange={(e) => setPortOfRegistrySearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {!showingCountries && (
                    <button 
                      type="button"
                      className="custom-select-option back-option" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("Back button clicked"); // Debug
                        handleBackToCountries();
                      }}
                      style={{ 
                        width: '100%', 
                        border: 'none', 
                        background: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer'
                      }}
                    >
                      ← Back to Countries
                    </button>
                  )}
                  {showingCountries ? (
                    filteredCountries.length > 0 ? (
                      filteredCountries.map((country, index) => (
                        <div
                          key={index}
                          className="custom-select-option"
                          onClick={() => handleCountrySelect(country)}
                        >
                          {country}
                        </div>
                      ))
                    ) : (
                      <div className="custom-select-option">No countries found</div>
                    )
                  ) : (
                    filteredPorts.length > 0 ? (
                      filteredPorts.map((port, index) => (
                        <div
                          key={index}
                          className="custom-select-option"
                          onClick={() => handlePortSelect(port)}
                        >
                          {port}
                        </div>
                      ))
                    ) : (
                      <div className="custom-select-option">No ports found</div>
                    )
                  )}
                </div>
              )}
            </div>
          </label>
          <label>
            Call Sign
            <input type="text" name="callSign" value={callSign} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Official Number
            <input type="text" name="officialNumber" value={officialNumber} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            IMO Number
            <input type="text" name="imoNumber" value={imoNumber} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            MMSI Number
            <input type="text" name="mmsiNumber" value={mmsiNumber} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Length Overall
            <input type="text" name="lengthOverall" value={lengthOverall} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Beam Moulded
            <input type="text" name="beamMoulded" value={beamMoulded} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Draft
            <input type="text" name="draft" value={draft} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Gross Tonnage
            <input type="text" name="grossTonnage" value={grossTonnage} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Cruising Speed
            <input type="text" name="cruisingSpeed" value={cruisingSpeed} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Range
            <input type="text" name="range" value={range} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Fuel Capacity
            <input type="text" name="fuelCapacity" value={fuelCapacity} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Propulsion
            <input type="text" name="propulsion" value={propulsion} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Generators
            <input type="text" name="generators" value={generators} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Water Capacity
            <input type="text" name="waterCapacity" value={waterCapacity} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Builder
            <input type="text" name="builder" value={builder} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Keel Laid
            <input type="text" name="keelLaid" value={keelLaid} onChange={handleChange} placeholder="Enter" />
          </label>
          {
            (vesselType === "AHTS 001") && (
              <label>
                Bollard Pull
                <input type="text" name="bollardPull" value={bollardPull} onChange={handleChange} placeholder="Enter" />
              </label>
            )
          }
          <label>
            BHP
            <input type="text" name="bhp" value={bhp} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Number of Passengers
            <input type="text" name="numberOfPassengers" value={numberOfPassengers} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Trackable
            <div className="custom-select-container" ref={trackableRef}>
              <button 
                type="button"
                className="custom-select-button"
                onClick={toggleTrackableDropdown}
              >
                {getTrackableLabel()}
              </button>
              {isTrackableOpen && (
                <div className="custom-select-dropdown show">
                  <div className="custom-select-search">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={trackableSearchTerm}
                      onChange={(e) => setTrackableSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredTrackableOptions.length > 0 ? (
                    filteredTrackableOptions.map((option, index) => (
                      <div
                        key={index}
                        className={`custom-select-option ${trackable === option.value ? 'selected' : ''}`}
                        onClick={() => handleTrackableSelect(option.value)}
                      >
                        {option.label}
                      </div>
                    ))
                  ) : (
                    <div className="custom-select-option">No options found</div>
                  )}
                </div>
              )}
            </div>
          </label>
        </div>

        <div className="button-row">
          <button className="back-btn" onClick={handleGoBack}>
            <HiArrowLeft style={{marginRight: 6}}/> Go Back
          </button>
          <button 
            className="next-btn" 
            onClick={handleSubmit} 
            disabled={isLoading}
          >
            {isLoading ? <Spinner /> : "Next"}
          </button>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default VesselGeneralInformation;


